import { useCallback, useSyncExternalStore } from 'react';

/**
 * Pinned "More" items shown directly on the sidebar rail
 * (clario-frontend `useSidebarRailPins` parity). clario-frontend persists
 * pins in the user-preferences store; the ecosystem persists them in
 * localStorage until the preferences endpoint is ported.
 *
 * Module-level store + useSyncExternalStore so the rail and the More
 * popover always see the same pin list.
 */

const STORAGE_KEY = 'clarix-sidebar-rail-pins';

function readPins(): readonly string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

let pins: readonly string[] = readPins();
const listeners = new Set<() => void>();

function writePins(next: readonly string[]) {
  pins = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private-mode failures */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSidebarRailPins() {
  const current = useSyncExternalStore(
    subscribe,
    () => pins,
    () => pins,
  );

  const isPinned = useCallback((id: string) => current.includes(id), [current]);
  const togglePin = useCallback(
    (id: string) => {
      writePins(current.includes(id) ? current.filter((p) => p !== id) : [...current, id]);
    },
    [current],
  );
  const unpin = useCallback((id: string) => writePins(current.filter((p) => p !== id)), [current]);

  return { pins: current, isPinned, togglePin, unpin };
}
